/**
 * Settings panel — security/behaviour policy (ADR-0027): SSRF egress posture
 * (HTTP + DB) + webhook strictness. DB-backed with permissive defaults; saving
 * stores only divergences. A "modificato" badge marks overridden fields. Gated
 * `settings:write`. Rendered as a panel inside the unified Settings page.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  type AdvancedSettings,
  type AdvancedSettingsValues,
  settingsApi,
} from "@/lib/api-settings";
import { usePermissions } from "@/lib/permissions";

function Section({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-surface p-5">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <p className="mb-4 mt-0.5 text-xs text-muted-foreground">{hint}</p>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Row({
  label,
  hint,
  overridden,
  overriddenLabel,
  control,
}: {
  label: string;
  hint?: string;
  overridden: boolean;
  overriddenLabel: string;
  control: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">{label}</span>
          {overridden && (
            <Badge tone="active" className="text-[10px]">
              {overriddenLabel}
            </Badge>
          )}
        </div>
        {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
      </div>
      <div className="shrink-0">{control}</div>
    </div>
  );
}

export function PolicySettings() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can("settings", "write");

  const q = useQuery({ queryKey: ["settings", "advanced"], queryFn: settingsApi.getAdvanced });
  const [form, setForm] = React.useState<AdvancedSettingsValues | null>(null);

  React.useEffect(() => {
    if (q.data) setForm(q.data.effective);
  }, [q.data]);

  const save = useMutation({
    mutationFn: (body: AdvancedSettingsValues) => settingsApi.putAdvanced(body),
    onSuccess: (data) => qc.setQueryData(["settings", "advanced"], data),
  });

  if (q.isError) {
    return (
      <p className="text-sm text-danger">
        {t("advancedSettings.loadError", "Impossibile caricare le impostazioni")}
      </p>
    );
  }

  const data: AdvancedSettings | undefined = q.data;
  if (!form || !data) {
    return <p className="text-sm text-muted-foreground">{t("common.loading", "Caricamento…")}</p>;
  }

  const isOverridden = (k: keyof AdvancedSettingsValues) => data.overridden.includes(k);
  const set = <K extends keyof AdvancedSettingsValues>(k: K, v: AdvancedSettingsValues[K]) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));
  const setLimit = (key: string, mb: number) =>
    setForm((f) =>
      f ? { ...f, upload_limits_mb: { ...f.upload_limits_mb, [key]: mb } } : f,
    );
  const dirty = (Object.keys(form) as (keyof AdvancedSettingsValues)[]).some(
    (k) => form[k] !== data.effective[k],
  );
  const ovr = t("advancedSettings.overridden", "modificato");

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-foreground">
            {t("advancedSettings.title", "Sicurezza & rete")}
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t(
              "advancedSettings.subtitle",
              "Policy di sicurezza e comportamento dell'istanza. Ogni valore ha un default permissivo; sovrascrivilo a caldo. Solo le modifiche vengono salvate.",
            )}
          </p>
        </div>
        {canWrite && (
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={save.isPending || !dirty}
              onClick={() => setForm(data.defaults)}
            >
              {t("advancedSettings.resetDefaults", "Valori predefiniti")}
            </Button>
            <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate(form)}>
              {save.isPending ? t("common.saving", "Salvataggio…") : t("common.save", "Salva")}
            </Button>
          </div>
        )}
      </div>

      <Section
        title={t("advancedSettings.ssrf.title", "Protezione SSRF — uscite di rete")}
        hint={t(
          "advancedSettings.ssrf.hint",
          "Loopback e l'endpoint cloud-metadata sono sempre bloccati. Gli intervalli privati (RFC-1918) sono bloccati solo in modalità rigida — utile se l'istanza non deve raggiungere la rete interna.",
        )}
      >
        <Row
          label={t("advancedSettings.field.httpBlockPrivate", "HTTP: blocca IP privati")}
          hint={t("advancedSettings.field.httpBlockPrivateHint", "Per http_request / http_sink / risorse HTTP delle app.")}
          overridden={isOverridden("http_block_private")}
          overriddenLabel={ovr}
          control={
            <Switch
              checked={form.http_block_private}
              disabled={!canWrite}
              onCheckedChange={(v) => set("http_block_private", v)}
            />
          }
        />
        <Row
          label={t("advancedSettings.field.httpAllowHosts", "HTTP: host consentiti")}
          hint={t("advancedSettings.field.allowHostsHint", "Eccezioni separate da virgola (hostname / IP / CIDR).")}
          overridden={isOverridden("http_allow_hosts")}
          overriddenLabel={ovr}
          control={
            <Input
              value={form.http_allow_hosts}
              disabled={!canWrite}
              onChange={(e) => set("http_allow_hosts", e.target.value)}
              placeholder="10.0.0.0/8, erp.local"
              className="w-64"
            />
          }
        />
        <Row
          label={t("advancedSettings.field.dbBlockPrivate", "DB: blocca host privati")}
          hint={t("advancedSettings.field.dbBlockPrivateHint", "Sull'host di una sorgente dati esterna prima della connessione.")}
          overridden={isOverridden("db_block_private")}
          overriddenLabel={ovr}
          control={
            <Switch
              checked={form.db_block_private}
              disabled={!canWrite}
              onCheckedChange={(v) => set("db_block_private", v)}
            />
          }
        />
        <Row
          label={t("advancedSettings.field.dbAllowHosts", "DB: host consentiti")}
          hint={t("advancedSettings.field.allowHostsHint", "Eccezioni separate da virgola (hostname / IP / CIDR).")}
          overridden={isOverridden("db_allow_hosts")}
          overriddenLabel={ovr}
          control={
            <Input
              value={form.db_allow_hosts}
              disabled={!canWrite}
              onChange={(e) => set("db_allow_hosts", e.target.value)}
              placeholder="10.0.0.0/8, replica.local"
              className="w-64"
            />
          }
        />
        <Row
          label={t("advancedSettings.field.dbConnectTimeout", "DB: timeout connessione (s)")}
          hint={t("advancedSettings.field.dbConnectTimeoutHint", "Evita che un host irraggiungibile blocchi test/preview.")}
          overridden={isOverridden("db_connect_timeout")}
          overriddenLabel={ovr}
          control={
            <Input
              type="number"
              min={1}
              max={300}
              value={form.db_connect_timeout}
              disabled={!canWrite}
              onChange={(e) => set("db_connect_timeout", Number(e.target.value))}
              className="w-24"
            />
          }
        />
      </Section>

      <Section
        title={t("advancedSettings.instance.title", "URL pubblico dell'istanza")}
        hint={t(
          "advancedSettings.instance.hint",
          "L'indirizzo pubblico con cui i servizi esterni raggiungono Tray. Usato per costruire il redirect URI di callback OAuth (es. server MCP con OAuth 2.1). Lascia vuoto per dedurlo dalla richiesta.",
        )}
      >
        <Row
          label={t("advancedSettings.field.publicBaseUrl", "URL pubblico (base)")}
          hint={t("advancedSettings.field.publicBaseUrlHint", "Es. https://trayos.dev — senza slash finale. Il callback sarà <url>/api/admin/ai/mcp-servers/oauth/callback.")}
          overridden={isOverridden("public_base_url")}
          overriddenLabel={ovr}
          control={
            <Input
              value={form.public_base_url}
              disabled={!canWrite}
              onChange={(e) => set("public_base_url", e.target.value)}
              placeholder="https://trayos.dev"
              className="w-72"
            />
          }
        />
      </Section>

      <Section
        title={t("advancedSettings.webhook.title", "Webhook in ingresso")}
        hint={t("advancedSettings.webhook.hint", "Severità nell'accettare richieste webhook in arrivo.")}
      >
        <Row
          label={t("advancedSettings.field.webhookStrict", "Modalità rigida")}
          hint={t("advancedSettings.field.webhookStrictHint", "Accetta solo sorgenti webhook registrate (le sconosciute → 404).")}
          overridden={isOverridden("webhook_strict")}
          overriddenLabel={ovr}
          control={
            <Switch
              checked={form.webhook_strict}
              disabled={!canWrite}
              onCheckedChange={(v) => set("webhook_strict", v)}
            />
          }
        />
        <Row
          label={t("advancedSettings.field.webhookHmacWindow", "Finestra anti-replay HMAC (s)")}
          hint={t("advancedSettings.field.webhookHmacWindowHint", "Tolleranza temporale per le richieste firmate HMAC.")}
          overridden={isOverridden("webhook_hmac_window")}
          overriddenLabel={ovr}
          control={
            <Input
              type="number"
              min={30}
              max={86400}
              value={form.webhook_hmac_window}
              disabled={!canWrite}
              onChange={(e) => set("webhook_hmac_window", Number(e.target.value))}
              className="w-24"
            />
          }
        />
      </Section>

      <Section
        title={t("advancedSettings.liveness.title", "Liveness dati — scheduler")}
        hint={t(
          "advancedSettings.liveness.hint",
          "Una sonda periodica verifica la raggiungibilità delle sorgenti esterne e ne aggiorna lo stato (online/offline).",
        )}
      >
        <Row
          label={t("advancedSettings.field.healthProbeEnabled", "Sonda di salute attiva")}
          hint={t("advancedSettings.field.healthProbeEnabledHint", "Controlla periodicamente ogni sorgente esterna abilitata.")}
          overridden={isOverridden("health_probe_enabled")}
          overriddenLabel={ovr}
          control={
            <Switch
              checked={form.health_probe_enabled}
              disabled={!canWrite}
              onCheckedChange={(v) => set("health_probe_enabled", v)}
            />
          }
        />
        <Row
          label={t("advancedSettings.field.healthProbeInterval", "Intervallo controllo (s)")}
          hint={t("advancedSettings.field.healthProbeIntervalHint", "Frequenza minima tra due controlli della stessa sorgente.")}
          overridden={isOverridden("health_probe_interval_seconds")}
          overriddenLabel={ovr}
          control={
            <Input
              type="number"
              min={60}
              max={86400}
              value={form.health_probe_interval_seconds}
              disabled={!canWrite || !form.health_probe_enabled}
              onChange={(e) => set("health_probe_interval_seconds", Number(e.target.value))}
              className="w-24"
            />
          }
        />
      </Section>

      <Section
        title={t("advancedSettings.aiBudget.title", "Budget AI mensile")}
        hint={t(
          "advancedSettings.aiBudget.hint",
          "Il tetto di spesa TOTALE per l'AI in un mese, su tutte le config, gli agenti e le squadre. È un totale perché la fattura arriva per chiave API, non per config: due config sulla stessa chiave sono la stessa bolletta. Raggiunto il tetto, ogni turno viene rifiutato con un messaggio onesto (mai una risposta inventata).",
        )}
      >
        <Row
          label={t("advancedSettings.field.aiMonthlyBudget", "Tetto mensile (USD)")}
          hint={t(
            "advancedSettings.field.aiMonthlyBudgetHint",
            "0 = nessun tetto. La spesa corrente è in Costi AI.",
          )}
          overridden={isOverridden("ai_monthly_budget_usd")}
          overriddenLabel={ovr}
          control={
            <Input
              type="number"
              min={0}
              value={form.ai_monthly_budget_usd}
              disabled={!canWrite}
              onChange={(e) => set("ai_monthly_budget_usd", Number(e.target.value))}
              className="w-32"
            />
          }
        />
      </Section>

      <Section
        title={t("advancedSettings.uploads.title", "Limiti di caricamento file (MB)")}
        hint={t(
          "advancedSettings.uploads.hint",
          "Dimensione massima per singolo file, per area. Vale per i caricamenti presenti e futuri; una nuova area compare qui automaticamente.",
        )}
      >
        {data.upload_entities.map((e) => {
          const cur = form.upload_limits_mb[e.key] ?? e.default_mb;
          return (
            <Row
              key={e.key}
              label={e.label}
              hint={t("advancedSettings.uploads.defaultHint", "Predefinito {{mb}} MB", { mb: e.default_mb })}
              overridden={cur !== e.default_mb}
              overriddenLabel={ovr}
              control={
                <div className="flex items-center gap-1.5">
                  <Input
                    type="number"
                    min={1}
                    max={1024}
                    value={cur}
                    disabled={!canWrite}
                    onChange={(ev) => setLimit(e.key, Number(ev.target.value))}
                    className="w-24"
                  />
                  <span className="text-xs text-muted-foreground">MB</span>
                </div>
              }
            />
          );
        })}
      </Section>

      {save.isError && (
        <p className="text-sm text-danger">{t("advancedSettings.saveFailed", "Salvataggio fallito.")}</p>
      )}
    </div>
  );
}
